'use client';

import React from 'react';
import {
  ParticipantTile,
  VideoTrack,
  ParticipantName,
  TrackMutedIndicator,
  useParticipantContext,
  useTrackRefContext,
  isTrackReference,
  ScreenShareIcon,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import type { ParticipantTileProps } from '@livekit/components-react';
import { ConnectionQualityWithStats } from './ConnectionStatsTooltip';

const AVATAR_PALETTE = [
  { color: '#9d7cff', bg: 'rgba(124, 92, 255, 0.14)', border: 'rgba(124, 92, 255, 0.42)' },
  { color: '#5590ff', bg: 'rgba(61,117,240,0.12)', border: 'rgba(61,117,240,0.35)' },
  { color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.35)' },
  { color: '#34d399', bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.35)' },
  { color: '#fb923c', bg: 'rgba(251,146,60,0.12)', border: 'rgba(251,146,60,0.35)' },
  { color: '#f472b6', bg: 'rgba(244,114,182,0.12)', border: 'rgba(244,114,182,0.35)' },
  { color: '#38bdf8', bg: 'rgba(56,189,248,0.12)', border: 'rgba(56,189,248,0.35)' },
];

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0].slice(0, 1)}${parts[parts.length - 1].slice(0, 1)}`.toUpperCase();
}

function getAvatarPalette(key: string) {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash + key.charCodeAt(i) * (i + 1)) % AVATAR_PALETTE.length;
  }
  return AVATAR_PALETTE[hash];
}

function CameraOffAvatar() {
  const participant = useParticipantContext();
  const displayName = participant.name || participant.identity || '?';
  const palette = getAvatarPalette(participant.identity || displayName);

  return (
    <div className="em-camera-off-avatar">
      <div
        className="em-camera-off-avatar__circle"
        style={{
          color: palette.color,
          background: palette.bg,
          borderColor: palette.border,
        }}
      >
        {getInitials(displayName)}
      </div>
    </div>
  );
}

function MicMutedBadge() {
  const participant = useParticipantContext();
  if (participant.isMicrophoneEnabled) {
    return null;
  }

  return (
    <span className="em-mic-muted-badge" aria-label="Microfone desligado">
      <TrackMutedIndicator
        trackRef={{ participant, source: Track.Source.Microphone }}
        show="muted"
      />
    </span>
  );
}

function TileContent() {
  const trackRef = useTrackRefContext();
  const participant = useParticipantContext();
  const isScreenShare = trackRef.source === Track.Source.ScreenShare;

  return (
    <>
      <div
        className="connection-quality-tile-wrapper"
        style={{
          position: 'absolute',
          top: 8,
          left: 8,
          zIndex: 20,
        }}
      >
        <ConnectionQualityWithStats participant={participant} />
      </div>

      {isTrackReference(trackRef) && (
        <VideoTrack trackRef={trackRef} />
      )}

      <div className="lk-participant-placeholder">
        <CameraOffAvatar />
      </div>

      <div
        className="lk-participant-metadata"
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '0.25rem 0.5rem',
        }}
      >
        <div className="lk-participant-metadata-item" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          {isScreenShare ? (
            <>
              <ScreenShareIcon style={{ width: '1rem', height: '1rem', flexShrink: 0 }} />
              <ParticipantName />
              {'\u2019s screen'}
            </>
          ) : (
            <>
              <MicMutedBadge />
              <ParticipantName />
            </>
          )}
        </div>
      </div>
    </>
  );
}

export function CustomParticipantTile(props: ParticipantTileProps) {
  return (
    <ParticipantTile {...props}>
      <TileContent />
    </ParticipantTile>
  );
}
